/**
 * Copyright 2023-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

export type State = {
  currentStep: number;
  subStep: number;
  planId: string;
  isPlanActive: boolean;
};

import {
  Action,
  ActionScope,
  ExecutionPolicy,
  SystemAction,
  PlanActionType,
  KeyValue,
  SkipWindowType,
  PlanActionNotificationGroupScope,
  PlanActionNotificationTriggerType,
  Maybe,
  PlanNotificationTriggerType,
  PlanActionProperties,
  ChangeDetails,
} from "~/generated/graphql";
import { Item } from "~/types";

type Actions = Partial<Action> | Partial<SystemAction>;

export enum FormField {
  LIST_ID = "listId",
  ID = "id",
  NAME = "name",
  ACTION_NAME = "actionName",
  BATCH_ID = "batchId",
  BATCH_ACTIONS = "batchActions",
  PLAN_BATCH_ACTIONS = "planBatchActions",
  DESCRIPTION = "description",
  METADATA = "metadata",
  SCHEDULE_TIMEZONE = "scheduleTimezone",
  SCHEDULE_OFFSET = "scheduleOffset",
  SCHEDULE_OFFSET_UNIT = "scheduleOffsetUnit",
  SCHEDULE_CRON = "scheduleCron",
  SCHEDULE_EXECUTION_DATES = "scheduleExecutionDates",
  PLAN_ENTITIES = "planEntities",
  RESOURCE_GROUP_IDS = "resourceGroupIds",
  CREATE_RESOURCE_GROUPS = "createResourceGroups",

  RESOURCE_IDS = "resourceIds",
  SCOPE = "scope",
  UNIT = "unit",
  EXECUTION_POLICY = "executionPolicy",
  RUN_IN_SEQUENCE = "runInSequence",
  SKIP_WINDOW = "skipWindow",
  ACTION_TYPE = "actionType",
  WINDOW_DURATION = "windowDuration",
  INPUT_PARAMETERS = "inputParameters",
  VERSION = "version",
  NOTIFICATION_GROUPS = "notificationGroups",
  NOTIFICATION_GROUP = "notificationGroup",
  NOTIFICATION_GROUPS_SCOPE = "notificationGroupsScope",
  TRIGGER_EVENT = "triggerEvent",
  NOTIFICATION_GROUPS_IDS = "notificationGroupIds",
  IN_ADVANCE = "inAdvance",
  IN_ADVANCE_UNIT = "inAdvanceUnit",
  NOTIFICATION_GROUPS_GENERAL = "notificationGroupsGeneral",
  PLAN_ENABLED = "planEnabled",
  EXECUTION_DATE = "executionDate",
  EXECUTION_TIME = "executionTime",
  CHANGE_DETAILS = "changeDetails",
}

export type CreateResourceGroup = {
  [FormField.LIST_ID]: string;
  [FormField.NAME]: string;
  [FormField.DESCRIPTION]?: string;
  [FormField.METADATA]?: KeyValue[];
  [FormField.RESOURCE_IDS]?: string[];
};

export type SelectedAction = Actions & {
  [FormField.LIST_ID]?: string;
  [FormField.BATCH_ID]?: string;
  [FormField.ACTION_NAME]?: string;
  [FormField.VERSION]?: string;
  [FormField.SCOPE]?: ActionScope;
  [FormField.RESOURCE_GROUP_IDS]?: string[];
  [FormField.RESOURCE_IDS]?: string[];
  [FormField.ACTION_TYPE]?: PlanActionType;
  [FormField.CREATE_RESOURCE_GROUPS]?: CreateResourceGroup[];

  [FormField.INPUT_PARAMETERS]?: KeyValue[];
  [FormField.NOTIFICATION_GROUPS]?: NotificationGroupItem[];
  [FormField.WINDOW_DURATION]?: string;
  [FormField.UNIT]?: TimeUnits;
  [FormField.EXECUTION_POLICY]?: ExecutionPolicy;
  [FormField.RUN_IN_SEQUENCE]?: boolean;
  [FormField.SKIP_WINDOW]?: SkipWindowType;
  [FormField.CHANGE_DETAILS]?: ChangeDetails;
} & {
  action?: PlanActionProperties;
};

type SelectedActionBatch = {
  [FormField.BATCH_ID]?: string;
  [FormField.BATCH_ACTIONS]?: SelectedAction[];
};

export type SelectedPlanEntity = Actions &
  SelectedAction &
  SelectedActionBatch & {
    selectedTab?: number;
  };

export type NotificationGroupItem = {
  [FormField.ID]?: string;
  [FormField.SCOPE]?: PlanActionNotificationGroupScope;
  [FormField.TRIGGER_EVENT]?: PlanActionNotificationTriggerType;
  [FormField.NOTIFICATION_GROUPS_IDS]?: string[];
  [FormField.NOTIFICATION_GROUP]?: {
    [FormField.ID]?: string;
    [FormField.NAME]?: string;
  };
  [FormField.IN_ADVANCE]?: Maybe<number>;
  [FormField.IN_ADVANCE_UNIT]?: TimeUnits;
};

export type NotificationGroupItemGeneral = {
  [FormField.ID]?: string;
  [FormField.TRIGGER_EVENT]?: PlanNotificationTriggerType;
  [FormField.NOTIFICATION_GROUPS_IDS]?: string[];
  [FormField.IN_ADVANCE]?: string;
  [FormField.UNIT]?: TimeUnits;
};

export type StepState = {
  entities: SelectedPlanEntity[];
  selectedEntity?: SelectedPlanEntity;
};

export type PlanEntity = Item & {
  icon?: string;
  name?: string;
  action?: PlanActionProperties;
  actionName?: string;
  [FormField.INPUT_PARAMETERS]?: KeyValue[];
  actionType?: PlanActionType;
  batchId?: string;
  batchActions?: SelectedPlanEntity[];
};

export enum TimeUnits {
  hours = "hours",
  minutes = "minutes",
  days = "days",
  weeks = "weeks",
}

export type Option = {
  label: string;
  value: string;
};

export type CronScheduleState = {
  timezone: string;
  cronExpression: string;
  offset: number;
  offsetUnit: TimeUnits;
  cronNextDates: string[];
};

export type DateItem = {
  [FormField.ID]: string;
  [FormField.EXECUTION_DATE]: string;
  [FormField.EXECUTION_TIME]: string;
};
